import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  Chip,
  TablePagination,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Badge,
  Stack,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { fetchNotifications, updateNotificationStatus, markAllNotificationsRead } from '../api/appApi';

dayjs.extend(relativeTime);

const isNotificationRead = (notif) => {
  if (!notif) return true;
  const status = String(notif.notificationStatus || '').toLowerCase();
  return status === 'check' || status === 'clicked' || status === 'read';
};

const resolveStaffRoute = (notif) => {
  const combined = `${notif.url || ''} ${notif.message || ''}`.toLowerCase();

  // 1. Queue / OPD Queue / Token / Waiting / Attending
  if (
    combined.includes('queue') ||
    combined.includes('waiting') ||
    combined.includes('token') ||
    combined.includes('attending') ||
    combined.includes('in-queue') ||
    combined.includes('in queue') ||
    combined.includes('opd')
  ) {
    return '/queue';
  }

  // 2. Appointments / Consultation / Booking / Rescheduled
  if (
    combined.includes('appointment') ||
    combined.includes('consultation') ||
    combined.includes('schedule') ||
    combined.includes('booking')
  ) {
    return '/appointments';
  }

  // 3. Tasks / Follow-ups / Assignments
  if (
    combined.includes('task') ||
    combined.includes('assigned') ||
    combined.includes('follow-up') ||
    combined.includes('followup') ||
    combined.includes('reminder')
  ) {
    return '/tasks';
  }

  // 4. Patients / Registration / Medical records
  if (
    combined.includes('patient') ||
    combined.includes('registered') ||
    combined.includes('medical record')
  ) {
    return '/patients';
  }

  // Default fallback to Dashboard
  return '/dashboard';
};

const NotificationsContainer = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('unread'); // 'unread' or 'all'

  const staffName = localStorage.getItem('staffName') || localStorage.getItem('STAFFNAME') || 'Staff Member';

  const handleNotificationClick = async (notif) => {
    // 1. Instantly remove notification from list if viewing unread, or mark checked
    if (activeTab === 'unread') {
      setNotifications((prev) => prev.filter((item) => item.id !== notif.id));
      setTotalRecords((prev) => Math.max(0, prev - 1));
      setUnreadTotal((prev) => Math.max(0, prev - 1));
    } else {
      setNotifications((prev) =>
        prev.map((item) => (item.id === notif.id ? { ...item, notificationStatus: 'check' } : item))
      );
    }

    if (notif.id) {
      try {
        // Mark as read in backend
        await updateNotificationStatus(notif.id, 'check');
      } catch (e) {
        console.error('Error marking notification as read:', e);
      }

      // Sync badge in header
      window.dispatchEvent(new Event('notificationsUpdated'));
    }

    // 2. Always navigate strictly within Staff App
    const staffRoute = resolveStaffRoute(notif);
    navigate(staffRoute);
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      if (activeTab === 'unread') {
        setNotifications([]);
        setTotalRecords(0);
      } else {
        setNotifications((prev) => prev.map((item) => ({ ...item, notificationStatus: 'check' })));
      }
      setUnreadTotal(0);
      window.dispatchEvent(new Event('notificationsUpdated'));
      loadNotifications();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const res = await fetchNotifications(page + 1, rowsPerPage, searchTerm);
      if (res && res.data) {
        const list = Array.isArray(res.data) ? res.data : [];
        const unreadCount = res.meta?.unreadCount !== undefined ? res.meta.unreadCount : list.filter((n) => !isNotificationRead(n)).length;
        setUnreadTotal(unreadCount);

        if (activeTab === 'unread') {
          const unreadList = list.filter((n) => !isNotificationRead(n));
          setNotifications(unreadList);
          setTotalRecords(unreadCount);
        } else {
          setNotifications(list);
          setTotalRecords(res.meta?.totalRecords || list.length || 0);
        }
      } else {
        setNotifications([]);
        setTotalRecords(0);
        setUnreadTotal(0);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setNotifications([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 8000);
    return () => clearInterval(interval);
  }, [page, rowsPerPage, searchTerm, activeTab]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Top Banner */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
            Hey! {staffName} 👋
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
            List of all your notifications, appointment alerts and clinical reminders!
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={<DoneAllIcon fontSize="small" />}
            onClick={handleMarkAllRead}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: theme.palette.text.primary,
              borderColor: theme.palette.divider,
              bgcolor: theme.palette.background.paper,
              '&:hover': { bgcolor: theme.palette.action.hover, borderColor: theme.palette.primary.main },
            }}
          >
            Mark all read
          </Button>

          <Tooltip title="Refresh notifications">
            <IconButton
              onClick={loadNotifications}
              sx={{
                bgcolor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '12px',
                p: 1.2,
                boxShadow: isDark ? 'none' : '0 2px 6px rgba(0,0,0,0.04)',
                '&:hover': { bgcolor: theme.palette.action.hover },
              }}
            >
              <RefreshIcon sx={{ color: theme.palette.text.primary }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Tabs Filter (Unread vs All) */}
      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 2.5 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => {
            setActiveTab(val);
            setPage(0);
          }}
          sx={{
            minHeight: 44,
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.primary.main,
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab
            value="unread"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Unread Alerts</span>
                {unreadTotal > 0 && (
                  <Chip
                    label={unreadTotal}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '11px',
                      fontWeight: 700,
                      bgcolor: '#ECFDF5',
                      color: '#059669',
                      border: '1px solid #A7F3D0',
                    }}
                  />
                )}
              </Box>
            }
            sx={{
              textTransform: 'none',
              fontWeight: activeTab === 'unread' ? 700 : 500,
              fontSize: '14px',
              minHeight: 44,
              color: activeTab === 'unread' ? theme.palette.primary.main : '#64748B',
            }}
          />
          <Tab
            value="all"
            label="All History"
            sx={{
              textTransform: 'none',
              fontWeight: activeTab === 'all' ? 700 : 500,
              fontSize: '14px',
              minHeight: 44,
              color: activeTab === 'all' ? theme.palette.primary.main : '#64748B',
            }}
          />
        </Tabs>
      </Box>

      {/* Search Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '16px',
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search notifications..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: '12px',
              bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC',
            },
          }}
        />
        {totalRecords > 0 && (
          <Chip
            label={`${totalRecords} Total`}
            size="small"
            sx={{
              fontWeight: 700,
              bgcolor: isDark ? 'rgba(79, 70, 229, 0.2)' : '#EEF2FF',
              color: isDark ? '#A5B4FC' : theme.palette.primary.main,
            }}
          />
        )}
      </Paper>

      {/* Notifications List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} />
        </Box>
      ) : notifications.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: '16px',
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
          }}
        >
          {activeTab === 'unread' ? (
            <>
              <CheckCircleOutlineIcon sx={{ fontSize: 48, color: '#10B981', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                All Caught Up! 🎉
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                You have reviewed and cleared all your pending notifications.
              </Typography>
            </>
          ) : (
            <>
              <NotificationsActiveIcon sx={{ fontSize: 48, color: theme.palette.divider, mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                No Notifications Found!
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                You have no history of notifications at this moment.
              </Typography>
            </>
          )}
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {notifications.map((notif, index) => {
            const isRead = isNotificationRead(notif);
            const timeAgo = notif.createdAt ? dayjs(notif.createdAt).fromNow() : '';

            return (
              <Card
                key={notif.id || index}
                elevation={0}
                onClick={() => handleNotificationClick(notif)}
                sx={{
                  borderRadius: '14px',
                  border: isRead
                    ? `1px solid ${theme.palette.divider}`
                    : `1.5px solid ${isDark ? '#4F46E5' : '#CBD5E1'}`,
                  bgcolor: isRead
                    ? theme.palette.background.paper
                    : (isDark ? 'rgba(79, 70, 229, 0.08)' : '#FAFCFF'),
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.04)' : '#F8FAFC',
                    transform: 'translateY(-1px)',
                    boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 14px rgba(99, 102, 241, 0.08)',
                  },
                }}
              >
                <CardContent sx={{ p: 2.2, '&:last-child': { pb: 2.2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexGrow: 1 }}>
                      {/* Indicator Dot: Green for Unread, Grey for Read */}
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: isRead ? (isDark ? '#475569' : '#CBD5E1') : '#10B981',
                          boxShadow: isRead ? 'none' : '0 0 0 3px rgba(16, 185, 129, 0.2)',
                          mt: 0.7,
                          flexShrink: 0,
                          transition: 'background-color 0.2s ease',
                        }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: isRead ? 500 : 700,
                            color: isRead ? theme.palette.text.secondary : theme.palette.text.primary,
                            lineHeight: 1.5,
                            fontSize: '14px',
                          }}
                        >
                          {notif.message}
                        </Typography>
                        {timeAgo && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.8 }}>
                            <AccessTimeIcon sx={{ fontSize: 13, color: theme.palette.text.secondary }} />
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: isRead ? 400 : 600 }}>
                              {timeAgo}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Right Arrow Icon */}
                    <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary, pr: 1 }}>
                      <ArrowForwardIosIcon sx={{ fontSize: 13 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}

          {/* Pagination */}
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: '14px',
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default NotificationsContainer;

